import { Component } from '@theme/component';

/**
 * @typedef {{ body: HTMLElement, widget: HTMLElement, toggle: HTMLButtonElement }} Refs
 */

/**
 * Starts the Loox reviews clipped on phones (assets/loox-reviews.css) with a button for the rest.
 * The iframe sets its own height late, so the button follows a ResizeObserver rather than a
 * measurement at load, and stays hidden when the reviews fit.
 *
 * @extends {Component<Refs>}
 */
class ReviewsClamp extends Component {
  requiredRefs = ['body', 'widget', 'toggle'];

  /** @type {ResizeObserver | null} */
  #observer = null;
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    // Tabbing into a clipped review opens the list, so focus never lands out of sight.
    this.addEventListener('focusin', this.#onFocusIn, { signal: this.#abortController.signal });
    this.#observer = new ResizeObserver(this.#measure);
    this.#observer.observe(this.refs.widget);
    this.#observer.observe(this.refs.body);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /** The "Read all reviews" button. */
  expand() {
    this.toggleAttribute('data-expanded', true);
    this.refs.toggle.hidden = true;
  }

  /** @param {FocusEvent} event */
  #onFocusIn = (event) => {
    if (event.target !== this.refs.toggle) this.expand();
  };

  #measure = () => {
    if (this.hasAttribute('data-expanded')) return;
    this.refs.toggle.hidden = this.refs.widget.offsetHeight <= this.refs.body.clientHeight + 1;
  };
}

if (!customElements.get('reviews-clamp')) customElements.define('reviews-clamp', ReviewsClamp);
